import { isEmailInDomains } from '@order-management/roles';
import { formatSalesOrderNumber } from '@order-management/format';
import {
  DeferredResponse,
  InputType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SalesOrder,
  SalesOrdersCriteria,
  SalesOrdersTableRow,
  SelectedSalesOrder,
  SortDirection,
} from '@order-management/types';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaArrowRight } from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSearchParams,
} from 'react-router-dom';
import { environment } from '../../../environments/environment';
import { fetchAvailableSalesOrders } from '../../sales-orders/sales-orders-service';
import SalesOrdersTable from '../../sales-orders/sales-orders-table/sales-orders-table';
import { resolveSalesOrderSummaryColumns } from '../../sales-orders/sales-orders-table/sales-orders-table-columns';
import CancelAlertModal from '../../shared/cancel-alert-modal/cancel-alert-modal';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useUpdateUrl from '../../shared/hooks/url';
import {
  createSearchParams,
  getSearchParamValue,
  getSearchParamValues,
} from '../../shared/url';
import { Button } from '../../ui/button/button';
import FeedbackMessage from '../../ui/feedback-message/feedback-message';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import SelectionIndicator from '../../ui/selection-indicator/selection-indicator';
import { OnTableChangeData, RowSelection } from '../../ui/table/types';
import { SalesOrdersStorageService } from '../sales-orders-storage-service';

const salesOrdersSessionStorageService = new SalesOrdersStorageService();

const NO_HARDWARE_ERROR_MESSAGE =
  'Work orders must contain at least one sales order with hardware';
const NO_SALES_ORDERS_ERROR_MESSAGE =
  'Work orders must contain at least one sales order';
const DIFFERENT_SALES_ORDERS_USER_ROLE_ERROR_MESSAGE =
  'Sales Orders from customers and zSpace cannot be contained in the same Work Order';
const CANCEL_ALERT_MODAL_TITLE = 'Cancel work order';
const CANCEL_ALERT_MODAL_SUBTITLE =
  'Are you sure you want to cancel the work order creation process?\nYou will lose all your progress';

const initialSalesOrderCriteria = (
  searchParams?: URLSearchParams
): SalesOrdersCriteria => ({
  itemsPerPage: parseInt(
    getSearchParamValue(searchParams, 'itemsPerPage') ?? '10'
  ),
  pageNumber: parseInt(getSearchParamValue(searchParams, 'pageNumber') ?? '1'),
  search: getSearchParamValue(searchParams, 'search') ?? '',
  sortBy: getSearchParamValue(searchParams, 'sortBy') ?? 'number',
  sortDirection:
    getSearchParamValue<SortDirection>(searchParams, 'sortDirection') ??
    SortDirection.DES,
  opportunities: getSearchParamValues(searchParams, 'opportunities'),
  accounts: getSearchParamValues(searchParams, 'accounts'),
  salesOrders: getSearchParamValues(searchParams, 'salesOrders'),
  endUser: getSearchParamValues(searchParams, 'endUser'),
  hardware: getSearchParamValue(searchParams, 'hardware') === 'true',
});

export const loader: LoaderFunction = ({ request }) => {
  const searchParams = createSearchParams(request);
  const criteria = initialSalesOrderCriteria(searchParams);

  const response = fetchAvailableSalesOrders(criteria);

  return defer({ response });
};

export function SelectSalesOrderPageContent() {
  const response = useAsyncValue() as PaginatedAPIResponse<SalesOrdersTableRow>;
  const navigation = useNavigation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const updateUrl = useUpdateUrl();

  const [nextButtonPressed, setNextButtonPressed] = useState(false);
  const [isCancelAlertModalVisible, setIsCancelAlertModalVisible] =
    useState(false);
  const [data, setData] = useState<SalesOrdersCriteria>(
    initialSalesOrderCriteria(searchParams)
  );
  const [salesOrders, setSalesOrders] = useState<SalesOrdersTableRow[]>(() =>
    salesOrdersSessionStorageService.get()
  );

  const selectedSalesOrders: SelectedSalesOrder[] = useMemo(
    () => salesOrders.map(transformToSelectedSalesOrder),
    [salesOrders]
  );

  const tableData: SalesOrdersTableRow[] = useMemo(
    () => response.data,
    [response]
  );

  const tableConfig: PaginatedContentConfig = useMemo(
    () => ({ ...data, pages: response.pages }),
    [data, response.pages]
  );

  const filtersData = useMemo(() => {
    return {
      salesOrders: {
        title: 'Sales order',
        appliedCount: data.salesOrders?.length,
        value: data.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      opportunities: {
        title: 'Opportunity',
        appliedCount: data.opportunities?.length,
        value: data.opportunities,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'opportunities',
          valueKey: 'opportunities',
          placeholder: 'Search by Opportunity',
        },
      },
      accounts: {
        title: 'Account',
        appliedCount: data.accounts?.length,
        value: data.accounts,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'accounts',
          valueKey: 'accounts',
          placeholder: 'Search by Account',
        },
      },
      endUser: {
        title: 'End user',
        appliedCount: data.endUser?.length,
        value: data.endUser,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'endUser',
          valueKey: 'endUser',
          placeholder: 'Search by End user',
        },
      },
    };
  }, [
    data.accounts,
    data.endUser,
    data.opportunities,
    data.salesOrders,
  ]) as FiltersData<SalesOrdersCriteria>;

  const customizeViewOptions = useMemo(
    () => [
      {
        title: 'Only show SO with hardware',
        applied: !!data.hardware,
        valueKey: 'hardware',
      },
    ],
    [data.hardware]
  );

  const rowSelection: RowSelection = useMemo(() => {
    return {
      onChange: (selectedRowIds) => {
        selectedRowIds.forEach((id) => {
          const foundRow = salesOrders.find((i) => i.id === id);

          if (foundRow) {
            setSalesOrders((oldSelectedSalesOrders) => {
              return oldSelectedSalesOrders.filter((so) => so.id !== id);
            });
          } else {
            const newSalesOrder = tableData.find((so) => so.id === id)!;
            setSalesOrders((oldSalesOrders) => {
              return [...oldSalesOrders, newSalesOrder];
            });
          }
        });
      },
      selectedRowKeys: tableData
        .filter((v) => salesOrders.map((s) => s.id).includes(v.id))
        .map((v) => v.id),
    };
  }, [salesOrders, tableData]);

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const hasSalesOrdersSelected = useMemo(
    () => salesOrders.length > 0,
    [salesOrders.length]
  );

  const showMissingSalesOrdersError = useMemo(
    () => !hasSalesOrdersSelected && nextButtonPressed,
    [hasSalesOrdersSelected, nextButtonPressed]
  );

  const hasDifferentSalesOrdersUserRoleSelected = useMemo(() => {
    const { internalCount, customerCount, genericCount } =
      selectedSalesOrders.reduce(
        (acc, so) => {
          if (!so.endUser) {
            acc.genericCount++;
          } else {
            const isInternalDomain = isEmailInDomains(
              so.endUser,
              environment.internalUserEmailDomains
            );
            if (isInternalDomain) {
              acc.internalCount++;
            } else {
              acc.customerCount++;
            }
          }
          return acc;
        },
        { internalCount: 0, customerCount: 0, genericCount: 0 } as {
          internalCount: number;
          customerCount: number;
          genericCount: number;
        }
      );

    return !(
      internalCount === selectedSalesOrders.length ||
      customerCount === selectedSalesOrders.length ||
      genericCount === selectedSalesOrders.length
    );
  }, [selectedSalesOrders]);

  const hasSalesOrderWithHardwareSelected = useMemo(
    () => selectedSalesOrders.some((so) => so.hardware),
    [selectedSalesOrders]
  );

  const showNoHardwareSalesOrderSelectedError = useMemo(
    () => !hasSalesOrderWithHardwareSelected && nextButtonPressed,
    [hasSalesOrderWithHardwareSelected, nextButtonPressed]
  );

  const isValidSalesOrderSelection = useMemo(
    () =>
      hasSalesOrdersSelected &&
      !hasDifferentSalesOrdersUserRoleSelected &&
      hasSalesOrderWithHardwareSelected,
    [
      hasDifferentSalesOrdersUserRoleSelected,
      hasSalesOrderWithHardwareSelected,
      hasSalesOrdersSelected,
    ]
  );

  const errorText = useMemo(() => {
    if (showMissingSalesOrdersError) {
      return NO_SALES_ORDERS_ERROR_MESSAGE;
    } else if (hasDifferentSalesOrdersUserRoleSelected) {
      return DIFFERENT_SALES_ORDERS_USER_ROLE_ERROR_MESSAGE;
    } else if (showNoHardwareSalesOrderSelectedError) {
      return NO_HARDWARE_ERROR_MESSAGE;
    }
  }, [
    hasDifferentSalesOrdersUserRoleSelected,
    showMissingSalesOrdersError,
    showNoHardwareSalesOrderSelectedError,
  ]);

  const emptyContentText = useMemo(() => {
    const hasAppliedFilters = data.search !== '' || appliedFilters.length > 0;
    if (hasAppliedFilters) {
      return 'There are no sales orders that match the selected criteria';
    }

    return 'There are no sales orders to select at the moment';
  }, [data.search, appliedFilters]);

  const isDataLoading = useMemo(
    () => navigation.state === 'loading',
    [navigation.state]
  );

  const handleOnTableChange = useCallback(
    async (value: OnTableChangeData<SalesOrdersTableRow>): Promise<void> => {
      const { pages, ...criteria } = value.config;
      const newData = {
        ...data,
        ...criteria,
        sortBy: value.column?.key || data.sortBy,
      };

      setData(newData);
      updateUrl(newData);
    },
    [data, updateUrl]
  );

  const handleTableFilterSubmit = useCallback(
    (tableFilterValue: SalesOrdersCriteria): void => {
      const newData = { ...data, ...tableFilterValue, pageNumber: 1 };
      setData(newData);
      updateUrl(newData);
    },
    [data, updateUrl]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof SalesOrdersCriteria)[]) => {
      const newData: SalesOrdersCriteria = { ...data, pageNumber: 1 };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialSalesOrderCriteria()[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      setData(newData);
      updateUrl(newData);
    },
    [data, updateUrl]
  );

  const navigateToWorkOrderList = useCallback(() => {
    navigate('/work-orders/dashboard/drafts');
  }, [navigate]);

  const navigateToCreateWorkOrderDetails = useCallback(() => {
    if (isValidSalesOrderSelection) {
      navigate('/work-orders/create/details');
    } else {
      setNextButtonPressed(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isValidSalesOrderSelection, navigate]);

  useEffect(() => {
    salesOrdersSessionStorageService.save(salesOrders);
  }, [salesOrders]);

  if (isDataLoading) {
    return (
      <Element display="flex" justifyContent="center">
        <PageSpinner />
      </Element>
    );
  }

  return (
    <>
      <Element display="flex" className="w-full pb-2" alignItems="center">
        <FilterBar
          initialData={initialSalesOrderCriteria()}
          data={data}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
          customizeViewOptions={customizeViewOptions}
        />
      </Element>
      <SalesOrdersTable
        dataSource={tableData}
        columns={resolveSalesOrderSummaryColumns(true)}
        onChange={handleOnTableChange}
        config={tableConfig}
        empty={
          <span className="my-2 is-block has-text-centered has-text-weight-light">
            {emptyContentText}
          </span>
        }
        form={{
          enableSearch: false,
          leftExtra: (
            <SelectionIndicator
              selected={selectedSalesOrders.length}
              total={response.total}
            />
          ),
        }}
        rowSelection={rowSelection}
        rowLabel={(el) => formatSalesOrderNumber(el.number)}
        errorMessage={
          errorText && (
            <Element mb={3}>
              <FeedbackMessage>{errorText}</FeedbackMessage>
            </Element>
          )
        }
      />

      <section className="is-flex is-justify-content-flex-end mt-8">
        <Button
          mr={4}
          color="primary-dark"
          outlined
          onClick={() => setIsCancelAlertModalVisible(true)}
        >
          Cancel
        </Button>
        <Button color="primary-dark" onClick={navigateToCreateWorkOrderDetails}>
          <span>Next</span>
          <Icon>
            <FaArrowRight />
          </Icon>
        </Button>
      </section>

      <CancelAlertModal
        show={isCancelAlertModalVisible}
        title={CANCEL_ALERT_MODAL_TITLE}
        subtitle={CANCEL_ALERT_MODAL_SUBTITLE}
        onCancel={navigateToWorkOrderList}
        onClose={() => setIsCancelAlertModalVisible(false)}
      />
    </>
  );
}

export function SelectSalesOrderPage() {
  const { response } = useLoaderData() as DeferredResponse<
    PaginatedAPIResponse<SalesOrder>
  >;

  return (
    <Suspense fallback={<PageSpinner />}>
      <ErrorHandlingAwait resolve={response}>
        <SelectSalesOrderPageContent />
      </ErrorHandlingAwait>
    </Suspense>
  );
}

export default SelectSalesOrderPage;

function transformToSelectedSalesOrder(
  salesOrder: SalesOrdersTableRow
): SelectedSalesOrder {
  return {
    ...salesOrder,
    hardware: salesOrder.devices.total > 0,
  };
}
